import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Database,
  Loader2,
  Pause,
  Play,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Simulated IDoc monitoring demo. All data is generated example data
 * (typical IDoc types and SAP status codes, fictitious document numbers and
 * anonymised partners) — clearly labelled as a demo in the UI.
 */

type Direction = "IN" | "OUT";
type Phase = "ok" | "error" | "analyzing" | "planned" | "fixing" | "fixed";
type Filter = "all" | "error" | "processing" | "success";

type ErrorPattern = {
  key: string;
  status: string;
  message: string;
  cause: string;
  plan: string;
  autonomous: boolean;
};

type Idoc = {
  id: number;
  docnum: string;
  type: string;
  message: string;
  partner: string;
  direction: Direction;
  time: string;
  status: string;
  phase: Phase;
  pattern?: ErrorPattern | undefined;
};

const TYPES: Array<{ type: string; message: string; direction: Direction }> = [
  { type: "ORDERS05", message: "ORDERS", direction: "IN" },
  { type: "DESADV01", message: "DESADV", direction: "OUT" },
  { type: "INVOIC02", message: "INVOIC", direction: "OUT" },
  { type: "MATMAS05", message: "MATMAS", direction: "OUT" },
  { type: "DEBMAS07", message: "DEBMAS", direction: "OUT" },
  { type: "ORDRSP", message: "ORDRSP", direction: "OUT" },
  { type: "WMMBID02", message: "WMMBXY", direction: "IN" },
];

const PARTNERS = ["CUSTOMER_A", "SUPPLIER_B", "EDI_HUB", "PLANT_1000", "3PL_NORTH", "CUSTOMER_C"];

const COPY = {
  en: {
    patterns: [
      { key: "material", status: "51", message: "Material 4711-B is not maintained for plant 1000", cause: "The order references a material whose plant view is missing.", plan: "Correct the material assignment in partner mapping and repost the IDoc (BD87).", autonomous: true },
      { key: "partner", status: "56", message: "Partner agreement for EDI_HUB / LS not found", cause: "WE20 has no inbound partner agreement for this message type.", plan: "Add the partner agreement, then process the IDoc again.", autonomous: false },
      { key: "price", status: "51", message: "Price variance exceeds tolerance at item 20", cause: "The transmitted price differs from pricing conditions.", plan: "Notify Sales; apply the condition record after confirmation.", autonomous: false },
      { key: "lock", status: "51", message: "Document 4500012345 is locked by a user", cause: "A temporary lock caused by parallel processing — a typical routine case.", plan: "Repost automatically after the lock is released.", autonomous: true },
      { key: "port", status: "02", message: "Error while transferring to port EDI_OUT", cause: "The outbound port was temporarily unavailable.", plan: "Check the connection and resend the IDoc (transaction BD88).", autonomous: true },
    ] satisfies ErrorPattern[],
    phase: { ok: "Processed", error: "Error detected", analyzing: "Analysis running", planned: "Awaiting approval", fixing: "Correction running", fixed: "Resolved" } satisfies Record<Phase, string>,
    filters: [{ value: "all", label: "All" }, { value: "error", label: "Errors" }, { value: "processing", label: "In progress" }, { value: "success", label: "Processed" }] satisfies Array<{ value: Filter; label: string }>,
    title: "AI IDOCMON · Live demo (PRD/100)", subtitle: "Continuous simulated IDoc traffic with anonymised sample data", autoMode: "Autonomous mode for enabled patterns", pause: "Pause", resume: "Resume",
    stats: ["IDocs in window", "Open errors", "In progress", "Resolved", "Autonomous"], filterLabel: "Filter IDocs by status", caption: "Simulated IDoc traffic", columns: ["IDoc", "Type", "Partner", "Status", "Time"], inbound: "Inbound", outbound: "Outbound", empty: "There are currently no IDocs with this status.",
    aiAnalysis: "AI analysis · high confidence", cause: "Cause", solution: "Proposed solution", execution: "Execution", autonomous: "Confirmed routine pattern — autonomous mode available", approval: "Approval required", audit: "Every correction remains within its defined permission scope, is validated and fully logged.", approve: "Approve correction", processed: "Reprocessed", logged: "logged", select: "Select a failed IDoc to see the cause and resolution plan.", disclosure: "Anonymised simulation of typical IDoc flows; document numbers, partners and business data are fictitious.", details: "View detailed demo", detailTo: "/solutions/ai-idocmon" as const,
  },
  tr: {
    patterns: [
      { key: "material", status: "51", message: "Malzeme 4711-B, 1000 üretim yeri için tanımlı değil", cause: "Sipariş, üretim yeri görünümü bulunmayan bir malzemeye referans veriyor.", plan: "Partner eşlemesindeki malzeme atamasını düzeltin ve IDoc'u yeniden kaydedin (BD87).", autonomous: true },
      { key: "partner", status: "56", message: "EDI_HUB / LS için partner anlaşması bulunamadı", cause: "WE20'de bu ileti türü için gelen partner anlaşması bulunmuyor.", plan: "Partner anlaşmasını ekleyin, ardından IDoc'u yeniden işleyin.", autonomous: false },
      { key: "price", status: "51", message: "20. kalemde fiyat farkı toleransı aşıyor", cause: "Aktarılan fiyat, fiyatlandırma koşullarından farklı.", plan: "Satış ekibini bilgilendirin; onaydan sonra koşul kaydını uygulayın.", autonomous: false },
      { key: "lock", status: "51", message: "4500012345 numaralı belge bir kullanıcı tarafından kilitli", cause: "Paralel işlemden kaynaklanan geçici kilit — tipik bir rutin durum.", plan: "Kilit kaldırıldıktan sonra otomatik olarak yeniden kaydedin.", autonomous: true },
      { key: "port", status: "02", message: "EDI_OUT portuna aktarım sırasında hata", cause: "Çıkış portuna kısa süreliğine erişilemedi.", plan: "Bağlantıyı kontrol edin ve IDoc'u yeniden gönderin (BD88 işlemi).", autonomous: true },
    ] satisfies ErrorPattern[],
    phase: { ok: "İşlendi", error: "Hata algılandı", analyzing: "Analiz sürüyor", planned: "Onay bekliyor", fixing: "Düzeltme sürüyor", fixed: "Çözüldü" } satisfies Record<Phase, string>,
    filters: [{ value: "all", label: "Tümü" }, { value: "error", label: "Hatalar" }, { value: "processing", label: "İşleniyor" }, { value: "success", label: "İşlendi" }] satisfies Array<{ value: Filter; label: string }>,
    title: "AI IDOCMON · Canlı demo (PRD/100)", subtitle: "Anonimleştirilmiş örnek verilerle sürekli simüle edilen IDoc trafiği", autoMode: "Etkinleştirilen desenler için otonom mod", pause: "Duraklat", resume: "Sürdür",
    stats: ["Penceredeki IDoc", "Açık hatalar", "İşleniyor", "Çözüldü", "Otonom"], filterLabel: "IDoc'ları duruma göre filtrele", caption: "Simüle edilmiş IDoc trafiği", columns: ["IDoc", "Tür", "Partner", "Durum", "Saat"], inbound: "Gelen", outbound: "Giden", empty: "Bu durumda IDoc bulunmuyor.",
    aiAnalysis: "AI analizi · yüksek güven", cause: "Neden", solution: "Önerilen çözüm", execution: "Uygulama", autonomous: "Doğrulanmış rutin desen — otonom mod etkinleştirilebilir", approval: "Onay gerekli", audit: "Her düzeltme tanımlanan yetki kapsamı içinde kalır, doğrulanır ve eksiksiz kaydedilir.", approve: "Düzeltmeyi onayla", processed: "Yeniden işlendi", logged: "kaydedildi", select: "Nedeni ve çözüm planını görmek için hatalı bir IDoc seçin.", disclosure: "Tipik IDoc akışlarının anonimleştirilmiş simülasyonu; belge numaraları, partnerler ve iş verileri kurgusaldır.", details: "Ayrıntılı demoyu görüntüle", detailTo: "/tr/cozumler/ai-idocmon" as const,
  },
} as const;

// Deterministic PRNG so server and client render the same initial list.
function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function okStatus(direction: Direction) {
  return direction === "IN" ? "53" : "16";
}

function makeIdoc(id: number, rand: () => number, time: string, errorRate: number, patterns: readonly ErrorPattern[]): Idoc {
  const t = TYPES[Math.floor(rand() * TYPES.length)]!;
  const partner = PARTNERS[Math.floor(rand() * PARTNERS.length)]!;
  const isError = rand() < errorRate;
  const pattern = isError ? patterns[Math.floor(rand() * patterns.length)] : undefined;
  return {
    id,
    docnum: String(40000000 + id * 37 + Math.floor(rand() * 30)).padStart(16, "0"),
    type: t.type,
    message: t.message,
    partner,
    direction: t.direction,
    time,
    status: pattern ? pattern.status : okStatus(t.direction),
    phase: pattern ? "error" : "ok",
    pattern,
  };
}

function clock(d: Date, locale: "en" | "tr") {
  return d.toLocaleTimeString(locale === "tr" ? "tr-TR" : "en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Berlin" });
}

function initialIdocs(count: number, patterns: readonly ErrorPattern[]): Idoc[] {
  const rand = rng(42);
  const list: Idoc[] = [];
  for (let i = 0; i < count; i++) {
    const s = 9 * 3600 + 12 * 60 + i * 7;
    const time = `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
    const idoc = makeIdoc(i + 1, rand, time, 0.18, patterns);
    // Older errors are already resolved to look like a running system.
    if (idoc.pattern && i < count - 3) {
      idoc.phase = "fixed";
      idoc.status = okStatus(idoc.direction);
    }
    list.unshift(idoc);
  }
  return list;
}

function filterGroup(phase: Phase): Exclude<Filter, "all"> {
  if (phase === "ok" || phase === "fixed") return "success";
  if (phase === "analyzing" || phase === "fixing") return "processing";
  return "error";
}

function StatusBadge({ idoc, phaseLabels }: { idoc: Idoc; phaseLabels: Record<Phase, string> }) {
  const tone =
    idoc.phase === "ok" || idoc.phase === "fixed"
      ? "bg-brand-green/10 text-foreground"
      : idoc.phase === "planned"
        ? "bg-accent text-accent-foreground"
        : idoc.phase === "analyzing" || idoc.phase === "fixing"
          ? "bg-primary/10 text-primary"
          : "bg-destructive/10 text-destructive";
  const Icon =
    idoc.phase === "ok" || idoc.phase === "fixed"
      ? CheckCircle2
      : idoc.phase === "analyzing" || idoc.phase === "fixing"
        ? Loader2
        : idoc.phase === "planned"
          ? Clock3
          : AlertTriangle;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${tone}`}>
      <Icon
        className={`h-3 w-3 ${idoc.phase === "analyzing" || idoc.phase === "fixing" ? "animate-spin" : ""}`}
        aria-hidden
      />
      {idoc.status} · {phaseLabels[idoc.phase]}
    </span>
  );
}

export function IdocMonitorDemo({ compact = false, locale = "en" }: { compact?: boolean; locale?: "en" | "tr" }) {
  const copy = COPY[locale];
  const rows = compact ? 6 : 10;
  const [idocs, setIdocs] = useState<Idoc[]>(() => initialIdocs(14, COPY[locale].patterns));
  const [running, setRunning] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [autoMode, setAutoMode] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const nextId = useRef(15);
  const randRef = useRef<() => number>(() => Math.random());

  useEffect(() => {
    randRef.current = rng(Date.now());
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) setRunning(false);
  }, []);

  // New traffic
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => {
      const idoc = makeIdoc(nextId.current++, randRef.current, clock(new Date(), locale), 0.22, copy.patterns);
      setIdocs((prev) => [idoc, ...prev].slice(0, 40));
    }, 2600);
    return () => window.clearInterval(t);
  }, [running, locale]);

  // Agent lifecycle: error -> analyzing -> planned (or autonomous fixing) -> fixed
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(() => {
      setIdocs((prev) =>
        prev.map((d) => {
          if (d.phase === "error") return { ...d, phase: "analyzing" };
          if (d.phase === "analyzing")
            return { ...d, phase: autoMode && d.pattern?.autonomous ? "fixing" : "planned" };
          if (d.phase === "fixing") return { ...d, phase: "fixed", status: okStatus(d.direction) };
          return d;
        }),
      );
    }, 1700);
    return () => window.clearInterval(t);
  }, [running, autoMode]);

  const stats = useMemo(() => {
    const total = idocs.length;
    const errors = idocs.filter((d) => filterGroup(d.phase) === "error").length;
    const processing = idocs.filter((d) => filterGroup(d.phase) === "processing").length;
    const fixed = idocs.filter((d) => d.phase === "fixed").length;
    const auto = idocs.filter((d) => d.phase === "fixed" && d.pattern?.autonomous).length;
    return { total, errors, processing, fixed, auto };
  }, [idocs]);

  const visible = idocs
    .filter((idoc) => filter === "all" || filterGroup(idoc.phase) === filter)
    .slice(0, rows);
  const selected =
    idocs.find((d) => d.id === selectedId) ??
    idocs.find((d) => d.phase === "planned") ??
    idocs.find((d) => d.pattern && d.phase !== "fixed") ??
    idocs.find((d) => d.pattern);

  const approve = (id: number) =>
    setIdocs((prev) => prev.map((d) => (d.id === id ? { ...d, phase: "fixing" } : d)));

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
      <div className="flex flex-col gap-4 border-b border-border px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-extrabold text-foreground">
            <Database className="h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span>{copy.title}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {copy.subtitle}
          </p>
        </div>
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <label className="flex min-h-8 items-center gap-2 text-xs font-bold text-muted-foreground">
            <input
              type="checkbox"
              checked={autoMode}
              onChange={(e) => setAutoMode(e.target.checked)}
              className="h-4 w-4 accent-[var(--primary)]"
            />
            {copy.autoMode}
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRunning((r) => !r)}
            aria-pressed={!running}
          >
            {running ? <Pause className="h-3.5 w-3.5" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
            {running ? copy.pause : copy.resume}
          </Button>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-px border-b border-border bg-border md:grid-cols-5">
        {[
          [copy.stats[0], stats.total],
          [copy.stats[1], stats.errors],
          [copy.stats[2], stats.processing],
          [copy.stats[3], stats.fixed],
          [copy.stats[4], stats.auto],
        ].map(([label, value]) => (
          <div key={label as string} className="bg-card px-4 py-3 sm:px-5 sm:py-4">
            <dt className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{label}</dt>
            <dd className="mt-1 text-2xl font-extrabold text-foreground tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="flex gap-2 overflow-x-auto border-b border-border px-4 py-3 sm:px-5" aria-label={copy.filterLabel}>
        {copy.filters.map((item) => (
          <Button
            key={item.value}
            type="button"
            variant={filter === item.value ? "default" : "secondary"}
            size="sm"
            onClick={() => setFilter(item.value)}
            aria-pressed={filter === item.value}
            className="shrink-0"
          >
            {item.label}
          </Button>
        ))}
      </div>

      <div className={compact ? "grid lg:grid-cols-[1.3fr_1fr]" : "grid lg:grid-cols-[1.2fr_1fr]"}>
        <div className="max-h-[30rem] overflow-auto border-b border-border lg:border-b-0 lg:border-r">
          <table className="w-full min-w-[580px] text-left text-sm">
            <caption className="sr-only">{copy.caption}</caption>
            <thead className="sticky top-0 z-10 bg-secondary text-xs uppercase text-muted-foreground">
              <tr>
                <th scope="col" className="px-4 py-2 font-bold">{copy.columns[0]}</th>
                <th scope="col" className="px-4 py-2 font-bold">{copy.columns[1]}</th>
                <th scope="col" className="px-4 py-2 font-bold">{copy.columns[2]}</th>
                <th scope="col" className="px-4 py-2 font-bold">{copy.columns[3]}</th>
                <th scope="col" className="px-4 py-2 font-bold">{copy.columns[4]}</th>
              </tr>
            </thead>
            <tbody aria-live="off">
              {visible.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => d.pattern && setSelectedId(d.id)}
                  className={`border-t border-border ${d.pattern ? "cursor-pointer hover:bg-secondary/70" : ""} ${selected?.id === d.id ? "bg-primary/5" : ""}`}
                >
                  <td className="px-4 py-2 font-mono text-xs text-foreground">
                    {d.pattern ? (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setSelectedId(d.id)}
                        className="h-auto px-0 font-mono text-xs"
                      >
                        {d.docnum.slice(-8)}
                      </Button>
                    ) : (
                      d.docnum.slice(-8)
                    )}
                  </td>
                  <td className="px-4 py-2 font-semibold text-foreground">
                    {d.type}
                    <span className="block text-xs font-normal text-muted-foreground">
                      {d.message} · {d.direction === "IN" ? copy.inbound : copy.outbound}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{d.partner}</td>
                  <td className="px-4 py-2"><StatusBadge idoc={d} phaseLabels={copy.phase} /></td>
                  <td className="px-4 py-2 text-xs tabular-nums text-muted-foreground">{d.time}</td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {copy.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="p-5" aria-live="polite">
          {selected?.pattern ? (
            <div className="text-sm">
              <p className="font-mono text-xs text-muted-foreground">
                {selected.docnum.slice(-8)} · {selected.message}/{selected.type} · PRD/100
              </p>
              <div className="mt-2 flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
                <h3 className="font-extrabold text-foreground">
                  Status {selected.pattern.status}: {selected.pattern.message}
                </h3>
              </div>
              <p className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-primary">
                <Sparkles className="h-4 w-4" aria-hidden /> {copy.aiAnalysis}
              </p>
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{copy.cause}</p>
                <p className="mt-1 leading-relaxed text-foreground">{selected.pattern.cause}</p>
              </div>
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{copy.solution}</p>
                <p className="mt-1 leading-relaxed text-foreground">{selected.pattern.plan}</p>
              </div>
              <div className="mt-4 rounded-md border border-border bg-secondary/50 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{copy.execution}</p>
                <p className="mt-2 flex items-center gap-1.5 font-bold text-foreground">
                {selected.pattern.autonomous ? (
                  <><Zap className="h-4 w-4 text-primary" aria-hidden /> {copy.autonomous}</>
                ) : (
                  <><ShieldCheck className="h-4 w-4 text-primary" aria-hidden /> {copy.approval}</>
                )}
                </p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {copy.audit}
                </p>
              </div>
              {selected.phase === "planned" ? (
                <Button
                  type="button"
                  onClick={() => approve(selected.id)}
                  className="mt-4 w-full font-extrabold"
                >
                  <ShieldCheck className="h-4 w-4" aria-hidden /> {copy.approve}
                </Button>
              ) : selected.phase === "fixed" ? (
                <p className="mt-4 flex items-center gap-1.5 font-semibold text-primary">
                  <CheckCircle2 className="h-4 w-4" aria-hidden /> {copy.processed} — Status {selected.status}, {copy.logged}
                </p>
              ) : (
                <p className="mt-4 flex items-center gap-1.5 font-semibold text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {copy.phase[selected.phase]} …
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{copy.select}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-xs leading-relaxed text-muted-foreground sm:px-5">
        <span>{copy.disclosure}</span>
        {compact && (
          <Link to={copy.detailTo} hash="live-demo" className="font-semibold text-primary hover:underline">
            {copy.details}
          </Link>
        )}
      </div>
    </div>
  );
}
